//Built-in libraries
import React from "react";

//Third party libraries
import { useFormContext } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { ArrowCircleLeftIcon } from "@heroicons/react/outline";
import { yupResolver } from "@hookform/resolvers/yup";

//Custom components
import { HookFormProvider } from "../../../components/UI/HookForm/Provider";
import { HookForm } from "../../../components/UI/HookForm/Form";
import { HookInput } from "../../../components/UI/HookForm/Form/Input";
import Button from "../../../components/UI/Button";

//Services
import { postForgotPassword } from "../../../api/user";

//Assets andd Miscellaneous
import { validationSchema } from "./validationSchema";

//Component Begin
interface ForgotPasswordFormProps {
  backHandler: (e: React.FormEvent) => void;
  forwardHandler: (type: string) => void;
}
const ForgotPasswordForm = ({
  backHandler,
  forwardHandler,
}: ForgotPasswordFormProps) => {
  const forgotPassword = useMutation(
    (data: { email: string }) => postForgotPassword(data),
    {
      onSuccess: () => {
        forwardHandler("forgotPassword");
        reset();
      },
    }
  );

  const { reset } = useFormContext();

  const onSubmit = async (data: { email: string }) => {
    const updatedData = { email: data.email.toLocaleLowerCase() };
    forgotPassword.mutate(updatedData);
  };

  const backButtonHandler = (e: React.FormEvent) => {
    reset();
    backHandler(e);
  };

  return (
    <HookForm
      onSubmit={onSubmit}
      noDefaultClass
      className="flex flex-col w-11/12 md:w-2/3 lg:w-3/5 max-w-md bg-white shadow-md rounded-md px-8 py-5 border-t-2"
    >
      <h2 className="mt-0">Gotcha! No worries, Let's reset your password</h2>
      <p className="mb-2">
        Please enter your email address registered on the SAGE terminal.
      </p>
      <HookInput
        name="email"
        label="Email"
        type="email"
        placeholder="Enter your email"
      />
      <Button
        customClassName="mt-5"
        label={`${
          forgotPassword.isLoading ? "Please wait..." : "Request Password Reset"
        }`}
        type="submit"
        disabled={forgotPassword.isLoading}
        btnStyle="btn-primary"
      />
      <div className="items-start mt-5">
        <button
          type="button"
          id="forgotPassword"
          className="flex flex-start items-center gap-1"
          onClick={backButtonHandler}
          disabled={forgotPassword.isLoading}
        >
          <ArrowCircleLeftIcon className="h-6 w-6 hover:text-yellow-400 transition duration-300 transform hover:-translate-x-1" />
          Back
        </button>
      </div>
    </HookForm>
  );
};

const ForgotPassword = (props: ForgotPasswordFormProps) => (
  <HookFormProvider
    defaultValues={{ email: "" }}
    mode="all"
    resolver={yupResolver(validationSchema)}
  >
    <ForgotPasswordForm {...props} />
  </HookFormProvider>
);

export default ForgotPassword;
